import * as dotProp from 'dot-prop-immutable';
import { Action } from 'typescript-fsa';

import { createReducer } from '@topconagriculture/frontend-utils';

import { SharingPartner } from '../../core/api/sharing/sharing.types';
import {
  addSelectedPartner,
  resetSelected,
  setCurrentFarm,
  setFocusedGroupField,
  setFocusedGroupId,
  setFocusedMatterField,
  setFocusedMatterId,
  setFocusedPremixId,
  setFocusedRecipeId,
  setSelectedBunkReaderPen,
  setSelectedCompany,
  setSelectedFeeding,
  setSelectedFeedingPeriod,
  setSelectedGroup,
  setSelectedIngredient,
  setSelectedLoadDeliveryRation,
  setSelectedLoadMixersPremix,
  setSelectedLotId,
  setSelectedMixer,
  setSelectedOrderId,
  setSelectedPremix,
  setSelectedRecipe,
  setSelectedReport,
  setSelectedReportFormat,
  setSelectedSettingsPanel,
  setSelectedWeighbridgeTicket,
} from './selected.actions';
import { LoadsDeliveriesRationContentForm } from '../../routes/Manage/DataTools/DataToolsPage/DataToolsSpecificPage/DataToolsSpecificPageContent/LoadDeliveriesDataTools/LoadDeliveriesContent/LoadsDeliveriesRationContent/LoadsDeliveriesRationContent';
import { SettingsType } from '../entities/settings/settings.types';

export type SelectedState = {
  readonly ingredientId: string | null;
  readonly premixId: string | null;
  readonly recipeId: string | null;
  readonly groupId: string | null;
  readonly feedingId: string | null;
  readonly feedingPeriodId: string | null;
  readonly mixerId: string | null;
  readonly reportId: string | null;
  readonly reportFormatId: string;
  readonly selectedPartner: SharingPartner | null;
  readonly currentFarmId: string | null;
  readonly companyId: string | null;
  readonly focusedGroupId: number;
  readonly focusedGroupField: number;
  readonly focusedRecipeId: number;
  readonly focusedPremixId: number;
  readonly focusedMatterId: number;
  readonly focusedMatterField: number;
  readonly selectedLoadDeliveryRation: LoadsDeliveriesRationContentForm | undefined;
  readonly selectedLotId: string | undefined;
  readonly selectedLoadMixersPremix: string | undefined;
  readonly seletedSettingsPanel: SettingsType;
  readonly selectedOrder: string | undefined;
  readonly selectedBunkReaderPen: string | null;
  selectedWeighbridgeTicket: string | null;
};

export const initialState: SelectedState = {
  ingredientId: null,
  premixId: null,
  recipeId: null,
  groupId: null,
  feedingId: null,
  feedingPeriodId: null,
  mixerId: null,
  reportId: null,
  reportFormatId: '1',
  selectedPartner: null,
  currentFarmId: null,
  companyId: null,
  focusedGroupId: -1,
  focusedGroupField: -1,
  focusedRecipeId: 0,
  focusedPremixId: 0,
  focusedMatterId: 0,
  focusedMatterField: -1,
  selectedLoadDeliveryRation: undefined,
  selectedLotId: undefined,
  selectedLoadMixersPremix: undefined,
  seletedSettingsPanel: SettingsType.driverChanges,
  selectedOrder: undefined,
  selectedBunkReaderPen: null,
  selectedWeighbridgeTicket: null,
};

const selectedReducer = createReducer(initialState, {
  [setSelectedIngredient.type]: (state: SelectedState, action: Action<number>) =>
    dotProp.set(state, 'ingredientId', action.payload),
  [setSelectedPremix.type]: (state: SelectedState, action: Action<number | null>) =>
    dotProp.set(state, 'premixId', action.payload),
  [setSelectedRecipe.type]: (state: SelectedState, action: Action<number | null>) =>
    dotProp.set(state, 'recipeId', action.payload),
  [setSelectedGroup.type]: (state: SelectedState, action: Action<number | null>) =>
    dotProp.set(state, 'groupId', action.payload),
  [setSelectedFeeding.type]: (state: SelectedState, action: Action<number | null>) =>
    dotProp.set(state, 'feedingId', action.payload),
  [setSelectedFeedingPeriod.type]: (state: SelectedState, action: Action<number | null>) =>
    dotProp.set(state, 'feedingPeriodId', action.payload),
  [setSelectedMixer.type]: (state: SelectedState, action: Action<number | null>) =>
    dotProp.set(state, 'mixerId', action.payload),
  [setSelectedReport.type]: (state: SelectedState, action: Action<number | null>) =>
    dotProp.set(state, 'reportId', action.payload),
  [setSelectedReportFormat.type]: (state: SelectedState, action: Action<number>) =>
    dotProp.set(state, 'reportFormatId', action.payload),
  [resetSelected.type]: (state: SelectedState) => ({
    ...state,
    ingredientId: null,
    premixId: null,
    recipeId: null,
    groupId: null,
    feedingId: null,
    feedingPeriodId: null,
    mixerId: null,
    focusedGroupId: -1,
    focusedGroupField: -1,
    focusedPremixId: 0,
    focusedPremixField: -1,
    focusedMatterId: -1,
    focusedMatterField: -1,
    selectedLotId: undefined,
    setSelectedOrder: undefined,
    selectedBunkReaderPen: null,
  }),
  [addSelectedPartner.type]: (state: SelectedState, action: Action<SharingPartner>) => {
    return dotProp.set(state, 'selectedPartner', action.payload);
  },
  [setCurrentFarm.type]: (state: SelectedState, action: Action<string | null>) => {
    return dotProp.set(state, 'currentFarmId', action.payload);
  },
  [setSelectedCompany.type]: (state: SelectedState, action: Action<string | null>) => {
    return dotProp.set(state, 'companyId', action.payload);
  },
  [setFocusedGroupId.type]: (state: SelectedState, action: Action<number>) => {
    return dotProp.set(state, 'focusedGroupId', action.payload);
  },
  [setFocusedGroupField.type]: (state: SelectedState, action: Action<number>) => {
    return dotProp.set(state, 'focusedGroupField', action.payload);
  },
  [setFocusedRecipeId.type]: (state: SelectedState, action: Action<number>) => {
    return dotProp.set(state, 'focusedRecipeId', action.payload);
  },
  [setFocusedPremixId.type]: (state: SelectedState, action: Action<number>) => {
    return dotProp.set(state, 'focusedPremixId', action.payload);
  },
  [setFocusedMatterId.type]: (state: SelectedState, action: Action<number>) => {
    return dotProp.set(state, 'focusedMatterId', action.payload);
  },
  [setFocusedMatterField.type]: (state: SelectedState, action: Action<number>) => {
    return dotProp.set(state, 'focusedMatterField', action.payload);
  },
  [setSelectedLoadDeliveryRation.type]: (
    state: SelectedState,
    action: Action<LoadsDeliveriesRationContentForm | undefined>,
  ) => {
    return dotProp.set(state, 'selectedLoadDeliveryRation', action.payload);
  },
  [setSelectedLotId.type]: (state: SelectedState, action: ReturnType<typeof setSelectedLotId>) => {
    return dotProp.set(state, 'selectedLotId', action.payload);
  },
  [setSelectedLoadMixersPremix.type]: (
    state: SelectedState,
    action: ReturnType<typeof setSelectedLoadMixersPremix>,
  ) => {
    return dotProp.set(state, 'selectedLoadMixersPremix', action.payload);
  },
  [setSelectedSettingsPanel.type]: (state: SelectedState, action: ReturnType<typeof setSelectedSettingsPanel>) => {
    return dotProp.set(state, 'seletedSettingsPanel', action.payload);
  },
  [setSelectedOrderId.type]: (state: SelectedState, action: ReturnType<typeof setSelectedOrderId>) => {
    return dotProp.set(state, 'selectedOrder', action.payload);
  },
  [setSelectedBunkReaderPen.type]: (state: SelectedState, action: ReturnType<typeof setSelectedBunkReaderPen>) => {
    return dotProp.set(state, 'selectedBunkReaderPen', action.payload);
  },
  [setSelectedWeighbridgeTicket.type]: (
    state: SelectedState,
    action: ReturnType<typeof setSelectedWeighbridgeTicket>,
  ) => {
    return dotProp.set(state, 'selectedWeighbridgeTicket', action.payload);
  },
});

export default selectedReducer;
